import type { AttendanceDashboardItem } from '@src/models/interfaces/attendanceDashboard.interface';
import {
  JOURNEY_ATTENDANCE_STATUS,
  REALTIME_AGGREGATE_STATUS,
  USER_ROW_ATTENDANCE_STATE,
  WORK_JOURNEY_REAL_TIME_TAB,
  type WorkJourneyRealTimeTab,
} from '@src/packages/workJourney/constants';

import {
  buildJourneyAttendanceRows,
  type JourneyStaffingByConfigId,
  matchesJourneyInstance,
  toUserStatusInput,
} from './attendanceMetrics';
import {
  isUserNotMarked,
  isUserPresent,
  resolveUserRowAttendanceState,
} from './attendanceStatus/resolveAttendanceStatus';

export interface RealTimeAttendanceFilters {
  workShiftId?: unknown;
  workJourneyConfigId?: unknown;
  employeeId?: unknown;
  status?: unknown;
  now?: Date;
  /** Which status model to match; defaults to the user-tab model. */
  statusScope?: WorkJourneyRealTimeTab;
}

export type FilterSelectOption = { value: string; label: string };

export interface RealTimeStructuralFilters {
  workShiftId?: unknown;
  workJourneyConfigId?: unknown;
  employeeId?: unknown;
}

export interface RealTimeStructuralFilterSelection {
  workShiftId: string;
  workJourneyConfigId: string;
  employeeId: string;
}

/**
 * SecondaryPanel is shared across screens; on remount the first render can still
 * see another screen's filter value (array/number/etc.). Only strings are applied.
 */
export const toFilterString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const TIME_AWARE_USER_STATUS_FILTERS = new Set<string>([
  REALTIME_AGGREGATE_STATUS.UNMARKED,
  USER_ROW_ATTENDANCE_STATE.NOT_STARTED,
  USER_ROW_ATTENDANCE_STATE.LATE,
  USER_ROW_ATTENDANCE_STATE.ABSENT,
]);

/** Journey membership can move with wall-clock time even without a Firestore write. */
const TIME_AWARE_JOURNEY_STATUS_FILTERS = new Set<string>(
  Object.values(JOURNEY_ATTENDANCE_STATUS),
);

/**
 * Status filters whose membership can change as wall-clock time passes.
 * ATTENDED is data-driven (marking), not clock-driven.
 */
export const isTimeAwareRealTimeStatusFilter = (
  status: unknown,
  statusScope: WorkJourneyRealTimeTab = WORK_JOURNEY_REAL_TIME_TAB.BY_USER,
): boolean => {
  const normalized = toFilterString(status);
  if (!normalized) {
    return false;
  }

  if (statusScope === WORK_JOURNEY_REAL_TIME_TAB.BY_JOURNEY) {
    return TIME_AWARE_JOURNEY_STATUS_FILTERS.has(normalized);
  }

  return TIME_AWARE_USER_STATUS_FILTERS.has(normalized);
};

export const applyStructuralFilters = (
  items: AttendanceDashboardItem[],
  filters: RealTimeStructuralFilters,
): AttendanceDashboardItem[] => {
  const workShiftId = toFilterString(filters.workShiftId);
  const workJourneyConfigId = toFilterString(filters.workJourneyConfigId);
  const employeeId = toFilterString(filters.employeeId);

  return items.filter(
    item =>
      (!workShiftId || item.workShiftId === workShiftId) &&
      (!workJourneyConfigId || item.workJourneyConfigId === workJourneyConfigId) &&
      (!employeeId || item.employeeId === employeeId),
  );
};

/**
 * Client-side filters for the Real-Time screen.
 *
 * Intentionally does NOT call Elastic / the attendanceDashboardQuery CF again.
 * Options for shift / journey / user should be built from the same loaded window
 * (see `buildRealTimeFilterOptionsFromItems`).
 *
 * Status matching is tab-scoped:
 * - Por usuario → `resolveUserRowAttendanceState` (ATTENDED / LATE / ABSENT / NOT_STARTED)
 * - Por jornada → journey-instance status via `buildJourneyAttendanceRows`
 */
export const filterAttendanceItems = (
  items: AttendanceDashboardItem[],
  filters: RealTimeAttendanceFilters,
  staffingByConfigId?: JourneyStaffingByConfigId,
): AttendanceDashboardItem[] => {
  const status = toFilterString(filters.status);
  const statusScope = filters.statusScope ?? WORK_JOURNEY_REAL_TIME_TAB.BY_USER;

  const result = applyStructuralFilters(items, filters);

  if (!status) {
    return result;
  }

  if (statusScope === WORK_JOURNEY_REAL_TIME_TAB.BY_JOURNEY) {
    const matchingInstances = buildJourneyAttendanceRows(result, staffingByConfigId).filter(
      row => row.status.code === status,
    );
    return result.filter(item =>
      matchingInstances.some(row => matchesJourneyInstance(item, row)),
    );
  }

  if (status === REALTIME_AGGREGATE_STATUS.UNMARKED) {
    return result.filter(item => isUserNotMarked({ ...toUserStatusInput(item), now: filters.now }));
  }

  if (status === REALTIME_AGGREGATE_STATUS.PRESENT) {
    return result.filter(item => isUserPresent({ ...toUserStatusInput(item), now: filters.now }));
  }

  return result.filter(
    item =>
      resolveUserRowAttendanceState({ ...toUserStatusInput(item), now: filters.now }).code ===
      status,
  );
};

/**
 * Builds select options only from items already loaded for the Real-Time window
 * (current calendar day), not from the full org catalog of shifts / journeys / users.
 */
export const buildRealTimeFilterOptionsFromItems = (
  items: AttendanceDashboardItem[],
  shiftNameById: ReadonlyMap<string, string> = new Map(),
): {
  shiftOptions: FilterSelectOption[];
  journeyOptions: FilterSelectOption[];
  userOptions: FilterSelectOption[];
} => {
  const shifts = new Map<string, string>();
  const journeys = new Map<string, string>();
  const users = new Map<string, string>();

  items.forEach(item => {
    if (item.workShiftId) {
      shifts.set(
        item.workShiftId,
        shiftNameById.get(item.workShiftId) ?? item.regulationName?.trim() ?? item.workShiftId,
      );
    }
    if (item.workJourneyConfigId) {
      journeys.set(item.workJourneyConfigId, item.journeyName?.trim() || item.workJourneyConfigId);
    }
    if (item.employeeId) {
      users.set(item.employeeId, item.userDisplayName?.trim() || item.employeeId);
    }
  });

  const toSortedOptions = (map: Map<string, string>): FilterSelectOption[] =>
    Array.from(map.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));

  return {
    shiftOptions: toSortedOptions(shifts),
    journeyOptions: toSortedOptions(journeys),
    userOptions: toSortedOptions(users),
  };
};

/**
 * Builds dependent select options from the loaded day window. Each field omits
 * itself from the structural filter so its selected value remains available.
 * Attendance status is deliberately excluded: it is time-derived and may change
 * while the filter drawer is open.
 */
export const buildRealTimeFilterFacetOptions = (
  items: AttendanceDashboardItem[],
  filters: RealTimeStructuralFilters,
  shiftNameById: ReadonlyMap<string, string> = new Map(),
): {
  shiftOptions: FilterSelectOption[];
  journeyOptions: FilterSelectOption[];
  userOptions: FilterSelectOption[];
} => {
  const { workShiftId, workJourneyConfigId, employeeId } = filters;
  const { shiftOptions } = buildRealTimeFilterOptionsFromItems(
    applyStructuralFilters(items, { workJourneyConfigId, employeeId }),
    shiftNameById,
  );
  const { journeyOptions } = buildRealTimeFilterOptionsFromItems(
    applyStructuralFilters(items, { workShiftId, employeeId }),
    shiftNameById,
  );
  const { userOptions } = buildRealTimeFilterOptionsFromItems(
    applyStructuralFilters(items, { workShiftId, workJourneyConfigId }),
    shiftNameById,
  );

  return { shiftOptions, journeyOptions, userOptions };
};

/**
 * Keeps persisted/shared panel values valid when the loaded day window changes.
 * The priority reflects the filter hierarchy: journey, then shift, then user.
 */
export const resolveConsistentRealTimeSelection = (
  items: AttendanceDashboardItem[],
  filters: RealTimeStructuralFilters,
): RealTimeStructuralFilterSelection => {
  const requestedJourney = toFilterString(filters.workJourneyConfigId);
  const hasValidJourney =
    !requestedJourney ||
    applyStructuralFilters(items, {
      workJourneyConfigId: requestedJourney,
    }).length > 0;
  if (!hasValidJourney) {
    return { workJourneyConfigId: '', workShiftId: '', employeeId: '' };
  }

  const workJourneyConfigId = requestedJourney;
  const requestedShift = toFilterString(filters.workShiftId);
  const hasValidShift =
    !requestedShift ||
    applyStructuralFilters(items, {
      workJourneyConfigId,
      workShiftId: requestedShift,
    }).length > 0;
  if (!hasValidShift) {
    return { workJourneyConfigId, workShiftId: '', employeeId: '' };
  }

  const workShiftId = requestedShift;
  const requestedEmployee = toFilterString(filters.employeeId);
  const employeeId = applyStructuralFilters(items, {
    workJourneyConfigId,
    workShiftId,
    employeeId: requestedEmployee,
  }).length
    ? requestedEmployee
    : '';

  return { workShiftId, workJourneyConfigId, employeeId };
};
